import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useSelector } from 'react-redux';

import { useIdleTimer } from 'src/hooks/useIdleTimer';
import { getDesksState } from 'src/store/desks/selectors';
import {
  bookDesk,
  desksExcludedDatesPollingStarted,
  desksExcludedDatesPollingStopped,
} from 'src/store/desks/services';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { Desk } from 'src/types';

import { DeskBookingFormValues } from './DeskBookingSidesheetPanel';

export interface DeskBookingFormProps {
  desk?: Desk;
  children?: ReactNode;
}

export const DeskBookingForm: FC<DeskBookingFormProps> = ({
  desk,
  children,
}) => {
  const dispatch = useAppDispatch();
  const { date } = useSelector(getDesksState);

  const book = useCallback(
    async (values: DeskBookingFormValues) => {
      await dispatch(
        bookDesk({
          dates: values.dates,
          bookedFor: values.bookedFor?.id as string | undefined,
        }),
      );
      return true;
    },
    [dispatch],
  );

  const startPolling = useCallback(() => {
    dispatch(desksExcludedDatesPollingStarted(false));
  }, [dispatch]);

  const stopPolling = useCallback(() => {
    dispatch(desksExcludedDatesPollingStopped());
  }, [dispatch]);

  useEffect(() => {
    if (desk?.id && !desk.status)
      dispatch(desksExcludedDatesPollingStarted(true));
  }, [desk?.id, desk?.status, dispatch]);

  useIdleTimer({
    idleMs: 60000,
    onPause: stopPolling,
    onResume: startPolling,
    when: !desk?.status,
  });

  const initialValues = useMemo((): DeskBookingFormValues => {
    return {
      dates: date ? new Set([date.slice(0, 10)]) : new Set(),
      bookedFor: null,
    };
  }, [date]);

  return (
    <Form onSubmit={book} initialValues={initialValues}>
      {() => children}
    </Form>
  );
};
